<template>
  <div class="kpis">
    <StatisticsKpi label="Utilisateurs" :value="integer(g.userCount)" :hint="g.deactivatedUserCount ? `${integer(g.deactivatedUserCount)} désactivé(s)` : 'Tous activés'" />
    <StatisticsKpi label="Utilisateurs actifs" :value="integer(g.activeUsers30d)" hint="Connectés dans les 30 derniers jours" />
    <StatisticsKpi label="Connexions (7 jours)" :value="integer(g.connections7d)" hint="Sept derniers jours glissants" />
    <StatisticsKpi label="Connexions (30 jours)" :value="integer(g.connections30d)" hint="Trente derniers jours glissants" />
    <StatisticsKpi label="Connexions (total)" :value="integer(g.totalConnections)" hint="Depuis le début du suivi" />
    <StatisticsKpi label="Dernière connexion" :value="dateTime(g.lastConnectionAt)" :hint="`Groupe créé le ${date(g.createdAt)}`" />
  </div>
  <div class="charts">
    <StatisticsCard :span="12" title="Connexions par jour" subtitle="Trente derniers jours" :empty="!hasData(data.series.map(row => row.connections))">
      <StatisticsChart :labels="data.series.map(row => shortDay(row.day))" :datasets="connectionDatasets" value-format="integer" />
    </StatisticsCard>
    <StatisticsCard :span="12" title="Utilisateurs" :empty="!data.users.length">
      <div class="table-scroll">
        <table class="users">
          <thead>
            <tr>
              <th>Identité</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Etat</th>
              <th>Dernière connexion</th>
              <th class="number">Connexions (30 j)</th>
              <th class="number">Connexions (total)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in data.users" :key="user.id">
              <td>{{ user.name }}</td>
              <td>{{ user.email }}<span v-if="!user.isVerify" class="muted"> (non vérifié)</span></td>
              <td>{{ ROLE_LABELS[user.role] ?? user.role }}</td>
              <td>{{ ACTIVATION_STATUS_ENUM[user.state]?.label ?? user.state }}</td>
              <td>{{ dateTime(user.lastConnectionAt) }}</td>
              <td class="number">{{ user.connections30d }}</td>
              <td class="number">{{ user.totalConnections }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </StatisticsCard>
    <StatisticsCard :span="12" title="Options" subtitle="Ajouter ou retirer une option du groupe">
      <GroupOptions :group="g" :options="data.options" :callback="callback" />
    </StatisticsCard>
    <StatisticsCard :span="12" title="Supprimer le groupe" subtitle="Action définitive">
      <p v-if="g.isSystem" class="danger-text">Ce groupe est un groupe technique de l'application : il ne peut pas être supprimé.</p>
      <template v-else>
        <p class="danger-text">Supprime le groupe, ses utilisateurs et toutes ses données (contrats, factures, achats, production, facturation électronique…). Cette action est irréversible.</p>
        <div class="danger-action">
          <CustomButton :params="{text: 'Supprimer le groupe'}" :isCompact="true" @click="$refs.deleteModal.openModal(g)" />
        </div>
      </template>
      <DeleteGroupModal ref="deleteModal" :callback="afterDelete" />
    </StatisticsCard>
  </div>
</template>

<script>
import StatisticsKpi from "@/statistics/components/StatisticsKpi.vue";
import StatisticsCard from "@/statistics/components/StatisticsCard.vue";
import StatisticsChart from "@/statistics/components/StatisticsChart.vue";
import {hasData, integer} from "@/statistics/utils/format";
import {PALETTE} from "@/statistics/utils/chartTheme";
import {ACTIVATION_STATUS_ENUM} from "@/models/activationStatusEnum";
import {ROLE_LABELS} from "@/models/roleEnum";
import GroupOptions from "@/administration/components/GroupOptions.vue";
import DeleteGroupModal from "@/administration/components/modals/DeleteGroupModal.vue";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import {date, dateTime, shortDay} from "@/administration/utils";

/**
 * Un groupe : ses utilisateurs, ses connexions et ses options (à ajouter ou à retirer).
 * callback : recharge le groupe après un changement d'options.
 */
export default {
  name: 'GroupDetail',
  components: {StatisticsKpi, StatisticsCard, StatisticsChart, GroupOptions, DeleteGroupModal, CustomButton},
  props: ['data', 'callback'],
  data() {
    return {ROLE_LABELS, ACTIVATION_STATUS_ENUM};
  },
  computed: {
    g() {
      return this.data.group;
    },
    connectionDatasets() {
      return [
        {label: 'Connexions', data: this.data.series.map(row => row.connections), color: PALETTE[0]},
        {label: 'Utilisateurs actifs', data: this.data.series.map(row => row.activeUsers), type: 'line', color: PALETTE[2]},
      ];
    },
  },
  methods: {
    integer, hasData, date, dateTime, shortDay,
    // The group doesn't exist anymore : back to the list
    afterDelete() {
      this.$router.push('/administration/groups');
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/statistics/style/statistics.styl';

.table-scroll
  overflow-x auto

.users
  width 100%
  border-collapse collapse
  font-size 13.5px

  th, td
    padding 9px 10px
    text-align left
    white-space nowrap

  th
    font-size 12.5px
    font-weight 600
    color CTRL_TEXT_MUTED
    border-bottom 1px solid #e5e5e5

  td
    color #3f3931
    border-bottom 1px solid #f1f3f8

  .number
    text-align right

.muted
  color CTRL_TEXT_MUTED
  font-size 12px

.danger-text
  margin 0 0 12px
  font-size 13.5px
  color CTRL_TEXT_MUTED

.danger-action
  width 190px
</style>
